<template>
  <div :id="mapId"></div>
</template>

<script lang="ts">
import {
  GeoJSONSource,
  GeolocateControl,
  LngLatBounds,
  LngLatBoundsLike,
  LngLatLike,
  Map,
  MapboxOptions,
  NavigationControl,
  Marker,
  Popup,
  AnySourceData,
  GeoJSONSourceOptions,
} from "mapbox-gl";
import { PropType } from "vue";
import { FeatureCollection, Point, Position, Feature } from "geojson";
import { storeToRefs } from "pinia";

import {
  IMapLngLat,
  IMapMarkersResponse,
  ISupplier,
  ISupplierPreview,
} from "~/types/map.interface";

import { getGeographicFeaturesByReverseGeocoding } from "~/api/mapRequest";
import { useMapFilterStore } from "~/stores/mapFilter";

const SUPPLIERS = "suppliers";
const MARKER_WIDTH = "80px";
const MARKER_HEIGHT = "80px";

export default defineNuxtComponent({
  props: {
    markers: {
      type: Object as PropType<FeatureCollection | IMapMarkersResponse>,
      default: [],
    },
    center: {
      type: Object as PropType<LngLatLike | Position>,
      default: null,
    },
    bounds: {
      type: Object as PropType<[
        IMapLngLat, // southwestern corner of the bounds
        IMapLngLat // northeastern corner of the bounds
      ] | []>,
      default: [],
    },
    zoom: {
      type: Number as PropType<number>,
      default: 14,
    },
    resize: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    geolocation: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    facilityPopupData: {
      type: [Object, null] as PropType<ISupplierPreview | null>,
      default: false,
    },
    isSelectedFacilityObject: {
      type: Boolean,
      default: false,
    },
    flyToCity: {
      type: Object as PropType<[IMapLngLat, IMapLngLat] | undefined>,
      default: undefined,
    },
    isCityButtonClicked: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "getCurrentBounds",
    "getFacilityPopupData",
    "update:facilityPopupData",
    "update:isSelectedFacilityObject",
    "update:isCityButtonClicked",
  ],

  setup(props, { emit }) {
    const SET_CITY_BREAKPOINT = 10;
    const CLUSTERS_BREAKPOINT = 15;

    const mapId = "map";
    const { gtag } = useGtag();
    const { mapboxKey } = useRuntimeConfig().public;
    const map = ref<Map>();
    const marker = ref<Marker>();
    const activePointId = ref<String>('');

    const router = useRouter();
    const route = useRoute();

    const mapFilterStore = useMapFilterStore();
    const { city } = storeToRefs(mapFilterStore);
    const { setMapFilterCity, resetMapFilterCity, resetMapFilterTags, resetMapFilterActivities } = mapFilterStore;

    const tooltip = new Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'map-tooltip',
      maxWidth: '300px',
      offset: 25,
      anchor: 'left'
    });

    function checkQueryParamsAndUpdatePopupData() {
      const { query } = route;
      const { supplier_id: supplierId, supplier_name: supplierName } = query;

      if (!supplierId || !supplierName) {
        return;
      }

      const marker = props.markers.features.find(
        (marker) => marker.properties?.id.toString() === supplierId
      );
      const geometry = <Point>marker?.geometry;
      const coordinates = geometry.coordinates;
      const { id, name, address } = <ISupplier>marker?.properties;

      emit("update:facilityPopupData", {
        id,
        name,
        address,
        coordinates,
      });
      emit("update:isSelectedFacilityObject", true);
    }

    function setCurrentBounds(bounds?: [IMapLngLat, IMapLngLat]): LngLatBounds {
      return new LngLatBounds([
        bounds?.[0] ?? props.bounds[0],
        bounds?.[1] ?? props.bounds[1],
      ] as [LngLatLike, LngLatLike]);
    }

    function getCurrentBounds() {
      return [
        [map.value?.getBounds().getWest(), map.value?.getBounds().getSouth()],
        [map.value?.getBounds().getEast(), map.value?.getBounds().getNorth()],
      ] as [IMapLngLat, IMapLngLat];
    }

    watch(
      () => props.markers,
      () => {
        if (map.value) {
          (map.value?.getSource(SUPPLIERS) as GeoJSONSource)?.setData(
            props.markers
          );
        }
      }
    );

    watch(
      () => props.bounds,
      () => {
        if (props.bounds && map.value) {
          map.value.fitBounds(setCurrentBounds());
        }
      }
    );

    watch(
      () => props.resize,
      () => {
        map.value?.resize();
      }
    );

    watch(
      () => props.markers,
      () => {
        if (!map.value) {
          return;
        }

        checkQueryParamsAndUpdatePopupData();
      },
      { once: true }
    );

    // do a `console.log(route)` to see route attributes (fullPath, hash, params, path...)
    watch(
      () => route.fullPath,
      async () => {
        checkQueryParamsAndUpdatePopupData();
      }
    );

    function addSelectedMarker(coordinates: LngLatLike) {
      if (map.value) {
        const el = document.createElement("div");

        el.className = "marker";
        el.style.backgroundImage = `url(/image/selectedMapMarker.webp)`;
        el.style.width = MARKER_WIDTH;
        el.style.height = MARKER_HEIGHT;
        el.style.backgroundSize = "100%";

        marker.value = new Marker(el).setLngLat(coordinates).addTo(map.value);
      }
    }

    function createTooltipContent(featureData: Feature) {
      if (map.value && featureData.geometry.type === "Point" && featureData.properties) {
        const { properties, geometry } = featureData;
        const { coordinates } = geometry;
        const { id } = properties;

        const isActivePoint = id === activePointId.value;

        if (!isActivePoint && Array.isArray(coordinates) && coordinates.length === 2) {
          tooltip.setLngLat(coordinates as [number, number])
            .setHTML(featureData.properties.name)
            .addTo(map.value);
        }
      }
    }

    watch(
      () => props.facilityPopupData,
      () => {
        marker.value?.remove();

        if(!props.facilityPopupData) {
          activePointId.value = '';
        }

        if (props.facilityPopupData && props.facilityPopupData.coordinates) {
          activePointId.value = String(props.facilityPopupData.id);
          addSelectedMarker(props.facilityPopupData.coordinates as LngLatLike);
          router.push({
            query: {
              supplier_id: props.facilityPopupData.id,
              supplier_name: props.facilityPopupData.name,
            },
            replace: false
          });

          if (props.isSelectedFacilityObject) {
            const currentZoom = map.value?.getZoom() ?? 0;
            map.value?.flyTo({
              center: props.facilityPopupData.coordinates,
              zoom: props.zoom > currentZoom ? props.zoom : currentZoom,
              offset: [100, 0],
            });

            emit("update:isSelectedFacilityObject", false);
          }
        }
      }
    );

    watch(
      () => props.flyToCity,
      () => {
        if (!map.value) {
          return;
        }

        if (!city.value?.id) {
          map.value.fitBounds(setCurrentBounds(props.bounds));
        } else {
          map.value.fitBounds(setCurrentBounds(props.flyToCity));
        }
      }
    );

    function toggleClusterMode() {
      if (!map.value) {
        return;
      }

      if (!map.value.isStyleLoaded()) {
        return;
      }

      const zoom = map.value.getZoom();
      const style = map.value.getStyle();
      const suppliers = <AnySourceData & GeoJSONSourceOptions>(
        style.sources.suppliers
      );

      if (suppliers) {
        suppliers.cluster = !(zoom && zoom >= CLUSTERS_BREAKPOINT);
      }

      map.value.setStyle(style);
    }

    async function handleMoveend() {
      if (!map.value) {
        return;
      }

      emit("update:isCityButtonClicked", false);

      const zoom = map.value.getZoom();
      if (zoom < SET_CITY_BREAKPOINT) {
        resetMapFilterCity();
        return;
      }

      const { lng, lat } = map.value.getCenter();
      const response = await getGeographicFeaturesByReverseGeocoding([
        lat,
        lng,
      ]);

      if (response) {
        setMapFilterCity({
          id: response.id,
          value: response.value,
        });
      } else {
        setMapFilterCity({
          id: "",
          value: "",
        });
      }
    }

    onMounted(() => {
      if (mapboxKey) {
        const mapBoxOptions:
          | { bounds: LngLatBoundsLike }
          | { center: LngLatLike | Position; zoom: number } =
          !!props.bounds && props.bounds?.length > 0
            ? { bounds: setCurrentBounds() }
            : { center: props.center, zoom: props.zoom };

        map.value = new Map({
          accessToken: mapboxKey as string,
          container: mapId,
          style: "mapbox://styles/mapbox/streets-v12",
          ...mapBoxOptions,
        } as MapboxOptions);

        // Set the minimum zoom level based on the bounds we receive
        // from the backend for the country
        if (props.bounds) {
          const camera = map.value.cameraForBounds(props.bounds as [LngLatLike, LngLatLike]);
          camera && map.value.setMinZoom(camera.zoom);
        }

        if (props.geolocation && !isIOS()) {
          map.value?.addControl(
            new GeolocateControl({
              positionOptions: {
                enableHighAccuracy: true,
              },
              trackUserLocation: true,
              showUserHeading: true,
            })
          );
        }

        map.value?.addControl(new NavigationControl({ showCompass: false }));

        if (props.bounds) {
          map.value.fitBounds(setCurrentBounds());
        }

        map.value?.loadImage(`/image/mapMarker.webp`, function (error, image) {
          if (error) throw error;
          if (image) {
            map.value?.addImage("map-marker", image);
          }
        });

        map.value?.loadImage(`/image/mapMarkerLimited.webp`, function (error, image) {
          if (error) throw error;
          if (image) {
            map.value?.addImage("map-marker-limited", image);
          }
        });

        map.value?.loadImage(
          `/image/selectedMapMarker.webp`,
          function (error, image) {
            if (error) throw error;
            if (image) {
              map.value?.addImage("selected-map-marker", image);
            }
          }
        );

        map.value.on("load", () => {

          map.value?.addSource(SUPPLIERS, {
            type: "geojson",
            data: props.markers,
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: 50,
          });

          map.value?.addLayer({
            id: "clusters",
            type: "circle",
            source: SUPPLIERS,
            filter: ["has", "point_count"],
            paint: {
              "circle-color": "#fff",
              "circle-radius": 20,
              "circle-stroke-width": 4,
              "circle-stroke-color": "#2581F8",
            },
          });

          map.value?.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: SUPPLIERS,
            filter: ["has", "point_count"],
            layout: {
              "text-field": "{point_count}",
              "text-font": ["Montserrat SemiBold"],
              "text-size": 16,
              "text-allow-overlap": true,
            },
          });

          map.value?.addLayer({
            id: "unclustered-point",
            type: "symbol",
            source: SUPPLIERS,
            filter: [
              "all",
              ["==", ["get", "isLimitedIcon"], false],
              ["!", ["has", "point_count"]]
            ],
            layout: {
              "icon-image": "map-marker",
              "icon-size": 0.4,
              "icon-allow-overlap": true,
            },
          });

          map.value?.addLayer({
            id: "unclustered-point-limited",
            type: "symbol",
            source: SUPPLIERS,
            filter: [
              "all",
              ["==", ["get", "isLimitedIcon"], true],
              ["!", ["has", "point_count"]]
            ],
            layout: {
              "icon-image": "map-marker-limited",
              "icon-size": 0.38,
              "icon-allow-overlap": true,
            },
          });

          marker.value?.setPopup(tooltip)
        });

        map.value?.on("click", "clusters", (e) => {
          gtag("event", MapEvents.cluster);
          const features = map.value?.queryRenderedFeatures(e.point, {
            layers: ["clusters"],
          });
          const clusterId = features?.[0].properties?.cluster_id;

          const source: GeoJSONSource = map.value?.getSource(
            SUPPLIERS
          ) as GeoJSONSource;
          source.getClusterExpansionZoom(
            clusterId,
            (err: any, zoom: number) => {
              if (err) return;
              let center;
              if (features?.[0].geometry.type === "Point") {
                center = features?.[0].geometry.coordinates;
              }
              map.value?.easeTo({
                center: center as LngLatLike,
                zoom: zoom + 2,
              });
            }
          );
        });

        map.value?.on("mouseenter", "clusters", () => {
          if (map.value instanceof Map) {
            map.value.getCanvas().style.cursor = "pointer";
          }
        });

        map.value?.on("mouseleave", "clusters", () => {
          if (map.value instanceof Map) {
            map.value.getCanvas().style.cursor = "";
          }
        });

        map.value?.on("click", ["unclustered-point", "unclustered-point-limited"], (e) => {
          gtag("event", MapEvents.facilityObjectModal);
          const geometry = e.features?.[0].geometry as Point;
          const coordinates = geometry?.coordinates || [];
          const { id, name, address } = e.features?.[0].properties as ISupplier;

          if (id === props.facilityPopupData?.id) {
            return;
          } else {
            marker.value?.remove();
          }
          activePointId.value = String(id);

          const facilityPopupData = {
            id,
            name,
            address,
            coordinates,
          };

          emit("update:facilityPopupData", facilityPopupData);

          if (coordinates.length) {
            let coordinateLng = coordinates[0];
            while (Math.abs(e.lngLat.lng - coordinateLng) > 180) {
              coordinateLng += e.lngLat.lng > coordinateLng ? 360 : -360;
            }
          }
        });

        map.value?.on("mouseenter", ["unclustered-point", "unclustered-point-limited"], (e) => {
          if (map.value instanceof Map) {
            if (e.features) {
              const feature = e.features[0] as Feature;
              createTooltipContent(feature);
            }
            map.value.getCanvas().style.cursor = "pointer";
          }
        });

        map.value?.on("mouseleave", ["unclustered-point", "unclustered-point-limited"], () => {
          if (map.value instanceof Map) {
            tooltip.remove()
            map.value.getCanvas().style.cursor = "";
          }
        });

        map.value.on("zoomend", () => {
          toggleClusterMode();
        });

        map.value.on("moveend", () => {
          handleMoveend();
        });

        emit("getCurrentBounds", getCurrentBounds());
      }
    });

    onUnmounted(() => {
      resetMapFilterCity();
      resetMapFilterTags();
      resetMapFilterActivities();
    });

    return {
      mapId,
    };
  },
});
</script>
<style scope scss>
#map {
  .mapboxgl-popup {
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
    padding: 0;
  }

  .mapboxgl-popup-content {
    background: #ffffff;
    border: none;
    border-radius: 12px;
    box-shadow: none;
    padding: 5px 16px;

    font-size: 18px;
    font-weight: 500;
    line-height: 22px;
  }

  .mapboxgl-popup-tip {
    display: none;
  }

  @media (max-width: 750px) {
    .map-tooltip {
      display: none;
    }
  }
}
</style>
